/**
 * An aquarium's reef (M45): where coral can grow, and what was measured on
 * it. Geography and survey record, code and not state, like the seafloor
 * (`seafloor.ts`); the coral living on it each day is state (M47).
 *
 * **Where it can be** is the floor from the shore to 30 m, the depths NOAA's
 * National Coral Reef Monitoring Program surveys (its stratified random
 * surveys sample hard bottom from 0 to 30 m) and to which NOAA's habitat maps
 * of the main Hawaiian Islands reach ("approximately 30 meters, depending on
 * water clarity", Battista et al. 2007): GEBCO's cells that deep, column by
 * column, in the program's three depth bins. How much of that floor is hard
 * bottom and how much sand is not known to the game (open, M45): the map that
 * says so, NOAA NCCOS's of 2007, was made from commercial satellite imagery
 * whose license its derived works inherit, and may not be carried. So the
 * game's coral is a cover, as the surveys measure it on hard bottom, not an
 * area.
 *
 * **What was measured** is the program's live coral cover, island by island
 * and depth bin by depth bin, survey by survey (`scripts/data/reef.ts`).
 *
 * **The water at depth** (M49): the heat a reef's coral feels is the
 * surface's (`heatStress.ts`) less how much cooler its water runs at its
 * depth on the days that count, MEASURED by the program's temperature
 * recorders on the reefs (`scripts/data/reefWater.ts`): a fall of so much a
 * meter, to each depth bin's coral's depth, the mean middle depth of the
 * surveys' sites in it. The coral's own warmest month is taken as the
 * surface's, not its water's, as the surveys' loss by depth in 2015 measured
 * (`scripts/coralHindcast.ts`).
 */

/** The islands a reef belongs to, in the surveys' order (definitions: the program's islands at Maui Nui). */
export const REEF_ISLANDS = ['maui', 'molokai', 'lanai', 'kahoolawe'] as const;
export type ReefIsland = (typeof REEF_ISLANDS)[number];

/** Each island's name as a player reads it. */
export const ISLAND_NAMES: Readonly<Record<ReefIsland, string>> = {
  maui: 'Maui',
  molokai: 'Molokaʻi',
  lanai: 'Lānaʻi',
  kahoolawe: 'Kahoʻolawe',
};

/**
 * The program's depth bins, by a site's middle depth: "Shallow = 0-6m, Mid =
 * 6-18m, Deep = 18-30m" (its data dictionary; definitions). A floor exactly
 * on a boundary belongs to the shallower bin.
 */
export const DEPTH_BINS = [
  { bin: 'shallow', name: 'shallow, to 6 m', bottomM: 6 },
  { bin: 'mid', name: 'mid-depth, 6 to 18 m', bottomM: 18 },
  { bin: 'deep', name: 'deep, 18 to 30 m', bottomM: 30 },
] as const;
export type DepthBin = (typeof DEPTH_BINS)[number]['bin'];

/** A depth bin's top, m: the bin above's bottom, the surface for the first (review M122: written three times before). */
export const binTopM = (bin: number): number => (bin === 0 ? 0 : (DEPTH_BINS[bin - 1] as (typeof DEPTH_BINS)[number]).bottomM);

/** A depth bin's span as a player reads it, from its top to its bottom: "0 to 6 m". */
export function binSpan(bin: number): string {
  return `${binTopM(bin)} to ${(DEPTH_BINS[bin] as (typeof DEPTH_BINS)[number]).bottomM} m`;
}

/** The reef's deepest floor, m: the deep bin's bottom. */
export const REEF_FLOOR_M = 30;

/** The depth bin a floor this deep, m, lies in, or null outside the reef's depths. */
export function depthBinOf(depthM: number): DepthBin | null {
  if (!(depthM > 0)) return null;
  for (const b of DEPTH_BINS) if (depthM <= b.bottomM) return b.bin;
  return null;
}

/** One stratum of a survey: an island's sites in one depth bin. */
export interface SurveyStratum {
  island: ReefIsland;
  bin: DepthBin;
  sites: number;
  /** The sites' mean live coral cover, percent of the bottom, or null where none was surveyed. */
  coverPercent: number | null;
  /** Its standard error, percentage points, or null under two sites. */
  sePercent: number | null;
}

/** One survey year at the site: when it ran and what each stratum found. */
export interface ReefSurvey {
  year: number;
  /** Its first and last site visits, and the middle one, as epoch days. */
  firstEpochDay: number;
  medianEpochDay: number;
  lastEpochDay: number;
  /** In `REEF_ISLANDS` by `DEPTH_BINS` order. */
  strata: readonly SurveyStratum[];
}

/** An aquarium's reef: its columns, each one's island and floor by depth bin, and the surveys. */
export interface Reef {
  /** Where the depths come from, where the surveys do and where the water at depth does, as the readout names them. */
  source: string;
  surveySource: string;
  waterSource: string;
  /** How much cooler the water is a meter deeper on the days that count toward Degree Heating Weeks, °C, above 0 (MEASURED, `scripts/data/reefWater.ts`). */
  hotDayCoolingCPerM: number;
  /** The same in the warmest month, every year, °C (MEASURED): the game does not read it; the reading the surveys turned down does (`scripts/coralHindcast.ts`). */
  warmestMonthCoolingCPerM: number;
  /** Each depth bin's coral's depth, m, in `DEPTH_BINS` order: the mean middle depth of the surveys' sites in it (MEASURED). */
  binDepthM: readonly number[];
  /** The seafloor's columns (row-major indices) holding any floor within the reef's depths, ascending. */
  columns: readonly number[];
  /** Each listed column's island, an index into `REEF_ISLANDS`: the island most of its reef floor lies nearest. */
  island: readonly number[];
  /** Each listed column's floor in each depth bin, km². */
  shallowKm2: readonly number[];
  midKm2: readonly number[];
  deepKm2: readonly number[];
  surveys: readonly ReefSurvey[];
}

/** Each depth bin's floor by column, km², in `DEPTH_BINS` order, each list in `reef.columns` order (review M50: gathered by hand in three places). */
export function binFloorsKm2(reef: Reef): ReadonlyArray<readonly number[]> {
  return [reef.shallowKm2, reef.midKm2, reef.deepKm2];
}

/** The index of a stratum, island by depth bin, in a survey's `strata`. */
export function stratumIndex(island: number, bin: number): number {
  return island * DEPTH_BINS.length + bin;
}

/** Each reef's floor by stratum, summed once: a reef is code, never changed, and the coral reads its floor every day (M49: the harness ran eight times slower summing it each time). */
const floors = new WeakMap<Reef, readonly number[]>();

/** The reef floor in each stratum, km², in `stratumIndex` order. */
export function stratumFloorKm2(reef: Reef): readonly number[] {
  const known = floors.get(reef);
  if (known) return known;
  const out = new Array<number>(REEF_ISLANDS.length * DEPTH_BINS.length).fill(0);
  const bins = binFloorsKm2(reef);
  reef.columns.forEach((_, i) => {
    const island = reef.island[i] as number;
    bins.forEach((b, j) => { out[stratumIndex(island, j)] = (out[stratumIndex(island, j)] as number) + (b[i] as number); });
  });
  floors.set(reef, Object.freeze(out));
  return out;
}

/** How much cooler than the surface each depth bin's water runs when the reef is under heat, °C, in `DEPTH_BINS` order (above). */
export function binHeatFallC(reef: Reef): number[] {
  return reef.binDepthM.map((d) => reef.hotDayCoolingCPerM * d);
}
